import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getStatus, normaliseBooksResponse, timeAgo, CoverArt, STATUS_LABELS, STATUS_COLORS } from "../utils/books";
import BookDetailDrawer from "./BookDetailDrawer";
import { AddForm } from "./AddBook";
import { extractChatMessageText, parseJsonReply } from "../utils/openai";
import ChatApp from "./ChatApp";
import Icon from "./Icons";

const GPT_MODEL = "gpt-5.4";

async function openaiChat(body) {
  const res = await axios.post(`/api/openai`, body);
  return res.data;
}

async function fetchCover(title, author) {
  try {
    const params = new URLSearchParams({ title, limit: 1, fields: 'cover_i' });
    if (author) params.set('author', author);
    const res = await axios.get(`https://openlibrary.org/search.json?${params}`);
    const coverId = res.data?.docs?.[0]?.cover_i;
    return coverId ? `https://covers.openlibrary.org/b/id/${coverId}-M.jpg` : null;
  } catch { return null; }
}

const TABS = [
  { key: 'all',       label: 'All' },
  { key: 'reading',   label: 'Reading' },
  { key: 'finished',  label: 'Finished' },
  { key: 'abandoned', label: 'Abandoned' },
  { key: 'wishlist',  label: 'Want to read' },
];

function LibraryCard({ book, onOpen, onStatusChange, onNote, onChat, onAddToCol, onDelete }) {
  const [menu, setMenu] = useState(false);
  const status  = getStatus(book);
  const hasNote = !!book.note?.trim();

  return (
    <div className="group relative flex flex-col bg-ink-surface/90 rounded-2xl overflow-hidden border border-ink-border hover:border-ink-muted transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lift"
         onClick={() => !menu && onOpen(book)}>
      {/* Cover */}
      <div className="relative w-full aspect-[2/3] overflow-hidden flex-shrink-0">
        <CoverArt book={book} className="w-full h-full transition-transform duration-700 group-hover:scale-[1.04]" />

        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/45 to-transparent opacity-70" />
        <span className={`absolute left-2.5 top-2.5 rounded-full border border-white/10 bg-ink-base/75 px-2 py-1 text-[9px] font-medium backdrop-blur-md ${STATUS_COLORS[status]}`}>
          {STATUS_LABELS[status]}
        </span>

        {status === 'reading' && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-ink-muted">
            <div className="h-full bg-gold" style={{ width: `${book.progress || 0}%` }} />
          </div>
        )}

        {/* Menu button */}
        <button
          onClick={e => { e.stopPropagation(); setMenu(o => !o); }}
          aria-label="Book actions"
          className="absolute top-2 right-2 w-8 h-8 rounded-xl bg-ink-base/80 flex items-center
                     justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all text-cream-muted hover:text-cream hover:bg-ink-base backdrop-blur-md"
        >
          <Icon name="more" size={16} />
        </button>

        {/* Dropdown */}
        {menu && (
          <div className="absolute top-8 right-2 bg-ink-raised border border-ink-border rounded-lg shadow-lift
                          text-xs overflow-hidden z-10 min-w-[140px]"
               onClick={e => e.stopPropagation()}
               onMouseLeave={() => setMenu(false)}>
            {TABS.filter(t => t.key !== 'all' && t.key !== status).map(t => (
              <button key={t.key} onClick={() => { onStatusChange(book, t.key); setMenu(false); }}
                className="w-full text-left px-3 py-2 text-cream-dim hover:bg-ink-muted hover:text-cream transition-colors">
                Move to {t.label}
              </button>
            ))}
            <div className="border-t border-ink-border" />
            {status === 'finished' && (
              <button onClick={() => { onChat(book); setMenu(false); }}
                className="w-full text-left px-3 py-2 text-cream-dim hover:bg-ink-muted hover:text-cream transition-colors">
                Chat with characters
              </button>
            )}
            <button onClick={() => { onNote(book); setMenu(false); }}
              className="w-full text-left px-3 py-2 text-cream-dim hover:bg-ink-muted hover:text-cream transition-colors">
              {hasNote ? 'Edit note' : 'Add note'}
            </button>
            <button onClick={() => { onAddToCol(book); setMenu(false); }}
              className="w-full text-left px-3 py-2 text-cream-dim hover:bg-ink-muted hover:text-cream transition-colors">
              Add to collection
            </button>
            <div className="border-t border-ink-border" />
            <button onClick={() => { onDelete(book); setMenu(false); }}
              className="w-full text-left px-3 py-2 text-crimson/70 hover:bg-ink-muted hover:text-crimson transition-colors">
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3.5 sm:p-4 flex flex-col gap-0.5 flex-1">
        <p className="text-cream text-sm font-semibold leading-snug line-clamp-2">{book.title}</p>
        <p className="text-cream-muted text-[11px] line-clamp-1">{book.author}</p>
        {status === 'reading' && book.progress > 0 && (
          <p className="text-cream-muted text-[10px]">{book.progress}% through</p>
        )}
        {status === 'finished' && book.finishDate && (
          <p className="text-cream-muted text-[10px]">Finished {timeAgo(book.finishDate)}</p>
        )}
        {status === 'finished' && !book.finishDate && (
          <p className="text-cream-muted text-[10px]">{timeAgo(book.addedOn)}</p>
        )}
        {status === 'finished' && book.rating > 0 && (
          <p className="text-gold text-[10px] tracking-tight">{'★'.repeat(book.rating)}</p>
        )}
        {hasNote && <p className="mt-1 inline-flex items-center gap-1 text-gold/70 text-[10px]"><Icon name="journal" size={11} /> Note saved</p>}
      </div>
    </div>
  );
}

function Library() {
  const [allBooks,    setAllBooks]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('all');
  const [search,      setSearch]      = useState('');
  const [sort,        setSort]        = useState('recent');
  const [selectedBook,  setSelectedBook]  = useState(null);
  const [isChatOpen,    setIsChatOpen]    = useState(false);
  const [noteBook,      setNoteBook]      = useState(null);
  const [note,          setNote]          = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [drawerBook,    setDrawerBook]    = useState(null);
  const [showAddForm,   setShowAddForm]   = useState(false);
  const [colPickBook,   setColPickBook]   = useState(null);
  const [userCols,      setUserCols]      = useState([]);
  const [colsLoaded,    setColsLoaded]    = useState(false);
  const [addedToCol,    setAddedToCol]    = useState(null);

  useEffect(() => { fetchBooks(); }, []);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      const res = await axios.get(`/api/books/${userId}`);
      setAllBooks(normaliseBooksResponse(res.data));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateBook = async (book, updates) => {
    await axios.post(`/api/books/update`, { id: book._id, ...updates });
    await fetchBooks();
  };

  const handleDelete = async (book) => {
    await axios.post(`/api/books/delete`, { id: book._id });
    setDrawerBook(null);
    await fetchBooks();
  };

  const handleAddBook = async ({ title, status, startDate, finishDate, progress }) => {
    const data = await openaiChat({
      model: GPT_MODEL,
      messages: [{ role: "user", content: `Provide ISBN, title, author, release_year for "${title}". Return JSON: {"ISBN":"...","title":"...","author":"...","release_year":"..."}` }],
      max_completion_tokens: 150, temperature: 0.3,
      response_format: { type: "json_object" },
    });
    let details;
    try { details = parseJsonReply(extractChatMessageText(data)); } catch { details = { title }; }
    details.imageLink = await fetchCover(details.title || title, details.author);
    const userId = localStorage.getItem("userId");
    await axios.post(`/api/books/add`, {
      userId,
      book: { ...details, status, progress, startDate: startDate || null, finishDate: finishDate || null, addedOn: Date.now() },
    });
    setShowAddForm(false);
    await fetchBooks();
  };

  const handleDrawerSave = async (updates) => {
    if (!drawerBook) return;
    await updateBook(drawerBook, updates);
  };

  const saveNote = async () => {
    if (!noteBook) return;
    await updateBook(noteBook, { note });
    setShowNoteModal(false);
  };

  const handleAddToCol = async (book) => {
    setColPickBook(book);
    if (!colsLoaded) {
      const userId = localStorage.getItem("userId");
      const { data } = await axios.get(`/api/collections/user?userId=${encodeURIComponent(userId)}`);
      setUserCols(data.collections || []);
      setColsLoaded(true);
    }
  };

  const addBookToCollection = async (col) => {
    const userId = localStorage.getItem("userId");
    await axios.post('/api/collections/addbook', {
      id: col._id, userId,
      book: {
        bookId: colPickBook._id,
        title:  colPickBook.title,
        author: colPickBook.author || '',
        imageLink: colPickBook.imageLink || '',
      },
    });
    setAddedToCol(col.title);
    setColPickBook(null);
    setTimeout(() => setAddedToCol(null), 2500);
  };

  const counts = {
    all:       allBooks.length,
    reading:   allBooks.filter(b => getStatus(b) === 'reading').length,
    finished:  allBooks.filter(b => getStatus(b) === 'finished').length,
    abandoned: allBooks.filter(b => getStatus(b) === 'abandoned').length,
    wishlist:  allBooks.filter(b => getStatus(b) === 'wishlist').length,
  };

  const filtered = allBooks
    .filter(b => tab === 'all' || getStatus(b) === tab)
    .filter(b => !search || b.title.toLowerCase().includes(search.toLowerCase()) ||
                            b.author?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sort === 'title')  return a.title.localeCompare(b.title);
      if (sort === 'author') return (a.author || '').localeCompare(b.author || '');
      return new Date(b.addedOn) - new Date(a.addedOn);
    });

  return (
    <div className="sc-page">
      <div className="max-w-7xl mx-auto pt-9 sm:pt-14">

        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="sc-kicker mb-2">Your archive</p>
            <h1 className="font-serif text-4xl font-semibold text-cream tracking-[-0.035em] sm:text-5xl">The library</h1>
            <p className="text-cream-muted text-sm mt-2">{allBooks.length} books, one evolving reading life.</p>
          </div>
          <button onClick={() => setShowAddForm(o => !o)} className="sc-btn-gold text-xs px-4 py-2.5">
            <Icon name={showAddForm ? "close" : "plus"} size={15} /> {showAddForm ? 'Close' : 'Add book'}
          </button>
        </div>

        {showAddForm && (
          <div className="mb-8">
            <AddForm allBooks={allBooks} onAdd={handleAddBook} onCancel={() => setShowAddForm(false)} />
          </div>
        )}

        {/* Search + sort */}
        <div className="sc-panel flex flex-col gap-2 p-2 mb-6 sm:flex-row">
          <div className="relative flex-1">
            <Icon name="search" size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-cream-muted" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search title or author…" className="sc-input border-transparent bg-transparent pl-10 flex-1 text-sm" />
          </div>
          <select value={sort} onChange={e => setSort(e.target.value)}
            aria-label="Sort library"
            className="sc-input w-full sm:w-auto text-sm text-cream-dim bg-ink-raised pr-8">
            <option value="recent">Recently added</option>
            <option value="title">Title A–Z</option>
            <option value="author">Author A–Z</option>
          </select>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-none mb-7 pb-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-shrink-0 rounded-xl border px-3.5 py-2 text-xs font-medium transition-all ${
                tab === t.key ? 'border-gold/30 bg-gold/10 text-gold' : 'border-ink-border bg-ink-surface/60 text-cream-muted hover:border-ink-muted hover:text-cream'
              }`}>
              {t.label}
              {counts[t.key] > 0 && (
                <span className={`ml-1.5 text-xs ${tab === t.key ? 'text-gold' : 'text-cream-muted/60'}`}>
                  {counts[t.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-5 xl:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="bg-ink-surface rounded-xl border border-ink-border aspect-[2/3] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="sc-panel py-20 px-6 text-center">
            <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold"><Icon name="search" size={20} /></span>
            <p className="font-serif text-2xl text-cream font-semibold mb-1">
              {search ? 'No matches' : `No ${tab === 'all' ? 'books' : TABS.find(t=>t.key===tab)?.label.toLowerCase() + ' books'} yet`}
            </p>
            {search && <p className="text-cream-muted text-sm mt-1">Try a different search</p>}
            {!search && tab === 'all' && (
              <Link to="/addbook" className="inline-block mt-4 sc-btn-gold text-sm">Add your first book</Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-5 xl:grid-cols-6">
            {filtered.map((book, i) => (
              <LibraryCard key={book._id || i} book={book}
                onOpen={b => setDrawerBook(b)}
                onStatusChange={(b, status) => updateBook(b, { status })}
                onNote={b => { setNoteBook(b); setNote(b.note || ''); setShowNoteModal(true); }}
                onChat={b => { setSelectedBook(b); setIsChatOpen(true); }}
                onAddToCol={handleAddToCol}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* "Added to collection" toast */}
      {addedToCol && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-ink-raised border border-ink-border
                        rounded-xl px-5 py-3 text-sm text-cream shadow-lift animate-fade-in">
          Added to <span className="text-gold font-medium">{addedToCol}</span>
        </div>
      )}

      {/* Collection picker modal */}
      {colPickBook && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
             onClick={() => setColPickBook(null)}>
          <div className="bg-ink-surface border border-ink-border rounded-2xl w-full max-w-sm p-5"
               onClick={e => e.stopPropagation()}>
            <p className="text-cream font-semibold text-sm mb-0.5">Add to collection</p>
            <p className="text-cream-muted text-xs mb-4 line-clamp-1">{colPickBook.title}</p>
            {userCols.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-cream-muted text-sm mb-3">No collections yet</p>
                <Link to="/collections" className="sc-btn-gold text-xs py-1.5 px-4"
                  onClick={() => setColPickBook(null)}>
                  Create one
                </Link>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {userCols.map(col => (
                  <button key={col._id} onClick={() => addBookToCollection(col)}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-ink-raised
                               border border-ink-border rounded-lg hover:border-gold/40 hover:bg-ink-muted
                               transition-colors text-left">
                    <div>
                      <p className="text-cream text-xs font-medium">{col.title}</p>
                      <p className="text-cream-muted text-[11px]">{col.books.length} books</p>
                    </div>
                    <span className="text-gold text-xs">Add →</span>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setColPickBook(null)}
              className="w-full mt-3 text-xs text-cream-muted hover:text-cream transition-colors py-1">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Note modal */}
      {showNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-ink-surface border border-ink-border rounded-xl w-full max-w-lg p-6">
            <p className="text-cream font-semibold mb-0.5">{noteBook?.title}</p>
            <p className="text-cream-muted text-xs mb-4">{noteBook?.author}</p>
            <textarea className="sc-input min-h-[120px] resize-none text-sm"
              placeholder="Your thoughts, quotes, reflections…"
              value={note} onChange={e => setNote(e.target.value)} />
            <div className="flex gap-2 mt-4 justify-end">
              <button className="sc-btn-ghost text-xs py-1.5 px-4" onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button className="sc-btn-gold text-xs py-1.5 px-4" onClick={saveNote}>Save</button>
            </div>
          </div>
        </div>
      )}

      {drawerBook && (
        <BookDetailDrawer
          book={drawerBook}
          onClose={() => setDrawerBook(null)}
          onSave={handleDrawerSave}
          onDelete={handleDelete}
        />
      )}

      <ChatApp
        isOpen={isChatOpen}
        onClose={() => { setIsChatOpen(false); setSelectedBook(null); }}
        selectedBook={selectedBook}
      />
    </div>
  );
}

export default Library;
